import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export default function Policies() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = async () => setRows((await api.get("/policies")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!creating.title || !creating.key) return toast.warning("Key + title required");
    await api.post("/policies", creating);
    toast.success("Created"); setCreating(null); load();
  };

  const newVersion = async () => {
    const changelog = prompt("What changed?") || "";
    await api.post(`/policies/${editing.policy_id}/version`, { body_markdown: editing.body_markdown, changelog });
    toast.success("Published new version"); setEditing(null); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Policies</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Versioned. Every version prompts a re-ack.</p>
        </div>
        <button onClick={() => setCreating({ key: "", title: "", body_markdown: "# ", mandatory: true })} className="tt-btn-primary flex items-center gap-2"><Plus size={14} /> New policy</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((p) => {
          const latest = p.versions[p.versions.length - 1];
          return (
            <div key={p.policy_id} className="tt-card p-4 cursor-pointer tt-card-hover" onClick={() => setEditing({ ...p, body_markdown: latest.body_markdown })}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium">{p.title}</div>
                <span className="tt-badge-neutral">v{latest.version}</span>
              </div>
              <div className="text-xs font-mono text-[#71717A]">{p.key} · {p.mandatory ? "mandatory" : "optional"}</div>
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-sm text-[#71717A]">No policies yet.</div>}
      </div>

      {creating && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setCreating(null)}>
          <div className="w-full max-w-xl tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h2 className="text-lg font-semibold">New policy</h2><button onClick={() => setCreating(null)} className="tt-btn-ghost"><X size={16} /></button></div>
            <input className="tt-input mb-3" placeholder="Key (e.g. leave, wfh, posh)" value={creating.key} onChange={(e) => setCreating({ ...creating, key: e.target.value })} />
            <input className="tt-input mb-3" placeholder="Title" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} />
            <textarea rows={8} className="tt-input font-mono text-xs mb-3" value={creating.body_markdown} onChange={(e) => setCreating({ ...creating, body_markdown: e.target.value })} />
            <div className="flex justify-end gap-2"><button onClick={() => setCreating(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={create} className="tt-btn-primary text-sm">Create</button></div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl tt-card p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h2 className="text-lg font-semibold">{editing.title}</h2><button onClick={() => setEditing(null)} className="tt-btn-ghost"><X size={16} /></button></div>
            <div className="tt-label mb-2">Latest version · v{editing.versions[editing.versions.length - 1].version}</div>
            <textarea rows={14} className="tt-input font-mono text-xs mb-4" value={editing.body_markdown} onChange={(e) => setEditing({ ...editing, body_markdown: e.target.value })} />
            <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="tt-btn-secondary text-sm">Close</button><button onClick={newVersion} className="tt-btn-primary text-sm">Publish new version</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
